/* META
{
  "slug": "how-to-negotiate-when-buying-jewelry",
  "title": "How to negotiate when buying jewelry, without annoying the seller",
  "description": "Reasoned offers get accepted and round-number lowballs get ignored. What to research first, how to phrase an offer, and when there is genuinely no room.",
  "topic": "Buying guides",
  "published": "2026-08-04",
  "updated": "2026-08-04",
  "readingMinutes": 6,
  "keywords": ["negotiate jewelry price", "make an offer jewelry", "haggle antique jewelry", "best offer etiquette", "buying antique jewelry price"],
  "summary": [
    "Research before you offer. A number attached to a reason gets a reply; a number attached to nothing gets ignored.",
    "Know the metal value, because it is the floor no seller will go below and it tells you how much room exists.",
    "Ask what else can move: postage, sizing, an extra piece, a chain included. Sellers who cannot drop the price often can add something."
  ],
  "faq": [
    {"q": "Is it rude to make an offer on jewelry?", "a": "Not at all, provided the offer is reasoned and the listing does not say the price is firm. What irritates sellers is an unexplained figure well below the market, sent without a question or a greeting. A short message giving your reasoning is received completely differently."},
    {"q": "How much below asking should I offer?", "a": "There is no universal percentage. Work out the metal value and check sold comparables, then offer something you can justify from those two numbers. On a piece priced close to its metal value there is almost no room; on a piece priced well above comparables there may be a great deal."},
    {"q": "What is a fair opening offer?", "a": "One you would accept yourself if the positions were reversed. Openings far below the market tend to end negotiations rather than start them, because they signal that agreement is unlikely and the seller stops spending time on it."},
    {"q": "What if the seller says no?", "a": "Accept it gracefully and say so. Sellers remember polite buyers, and a piece that does not sell in three weeks often produces a message offering it at your number. Arguing after a refusal removes that possibility entirely."},
    {"q": "Can I negotiate on things other than price?", "a": "Frequently, and it is underused. Free or upgraded shipping, resizing included, a chain thrown in with a pendant, a bundle discount on two pieces, or holding an item for a few days. Sellers protecting a headline price are often happy to add value elsewhere."}
  ],
  "related": ["how-to-handle-offers-and-negotiation", "how-to-price-used-jewelry", "questions-to-ask-before-buying-jewelry-online"],
  "image": {"src": "/static/blog/how-to-negotiate-when-buying-jewelry.jpg", "alt": "Sublime curb chain, 30 grams of 14K gold, 1955 to 1973, from the Oldmine collection", "product": "sublime-30-grams-curb-chain-14k-gold-1955-73", "imgfile": "BD88B82F-F615-422D-BB86-3D35B54D796A.jpg", "caption": "Thirty grams of 14K. On a piece like this the metal price does most of the negotiating for both of you."}
}
META */
// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
Negotiating well in this market is mostly about arriving informed. Sellers deal with a steady stream of unexplained lowball messages, and they have learned to ignore them, so the whole game is separating yourself from that stream in your first sentence.

## Do the work first

**Find the metal value.** Weight in grams, fineness, current metal price. This is the floor: no informed seller will let solid gold go below what it is worth melted, and knowing the number tells you immediately how much room exists. On a heavy chain priced near its metal value, there is almost none, and asking for thirty per cent off marks you as someone who has not looked.

**Check sold comparables.** Not asking prices, which contain hope. What have similar pieces actually sold for.

**Identify what is uncertain.** Missing marks, undisclosed condition, no measurements. Genuine gaps are legitimate grounds for a lower offer, and naming them gives your number a reason.

## How to phrase it

Compare these two messages.

*Would you take 400?*

*Hello. I am interested in the bangle. Two similar Danish 14K bangles of about this weight sold recently at around 480 and 510, and this one has the dent to the underside you have photographed. Would you consider 450? Happy either way, and thank you for the clear pictures.*

The second one gets a reply nearly every time, because it demonstrates that you have looked, it gives the seller something to respond to, and it makes accepting feel like agreement rather than defeat. The first one gets deleted.

## The rules that work

| Do | Do not |
| --- | --- |
| Give a reason for your number | Send a bare figure |
| Ask questions before offering | Offer before understanding what it is |
| Make one considered offer | Grind down in three successive steps |
| Be ready to pay immediately | Negotiate and then go quiet |
| Accept a no gracefully | Argue, or return with the same number |

That fourth row matters more than people realise. A buyer who negotiates hard and then delays payment is expensive, and sellers who have been through it once become inflexible with everyone afterwards.

## When there is no room

Be able to recognise these, because pressing on them wastes everyone's time:

- The price is close to the metal value.
- The piece is already priced below sold comparables.
- The listing says the price is firm.
- The seller is a dealer with fixed margins and stated terms.
- It has been listed for two days. Nobody discounts in the first week.

Conversely, room usually exists when a piece has been listed for months, when it is priced well above comparables, when the seller is clearing a collection, or when you are buying more than one thing.

## Negotiate on the other variables

This is the most underused move in the whole subject. A seller who cannot drop a headline price will very often:

- include or upgrade shipping
- include resizing, or contribute to it
- add a chain to a pendant
- discount a second piece
- hold an item for a few days while you decide

Ask. The worst outcome is a polite no, and you have lost nothing.

## The last thing

Say thank you and mean it, whether or not you agree a price. This market is small, sellers remember buyers, and a piece that has not moved in a month frequently produces a message that says: *are you still interested at the number you mentioned?* That message only ever goes to people who were pleasant the first time.
`;

export default body;
